import { Alert, Col, Empty, Row } from 'antd';
import type { OverviewResponse } from '../contract';
import { KIND_LABEL, fmtDay, fmtHours, fmtKc, fmtNum, monthFileScope } from '../format';
import AtRiskList from './at-risk-list';
import { DailyLine } from './daily-line';
import InsightsCard from './insights-card';
import KpiCard from './kpi-card';
import MixDonut, { Donut } from './mix-donut';
import MonthHeatmap from './month-heatmap';
import RankingList from './ranking-list';
import TopBars from './top-bars';
import EfficiencySection from './efficiency-section';
import FansSection from './fans-section';
import MoreKpis, { kpiCompareProps } from './more-kpis';
import OverseasSection from './overseas-section';
import { AgentTable, CohortsCard } from './people-section';
import StatusDonut from './status-donut';
import { TiktokMonthPanel } from './tiktok-month-panel';
import styles from '../style.module.scss';

export const NO_SERIES =
  'Tháng này chỉ còn số tổng (chuỗi theo ngày chỉ giữ cho tháng hiện tại và tháng trước).';
export const NO_SERIES_MONTH_FILE =
  'Tháng này tính theo file tháng TikTok — không có số theo ngày.';

interface Props {
  ov: OverviewResponse;
  openCreator: (uid: string) => void;
}

/** Các khối Tổng quan (đặc tả §8 mục 1–7): độ phủ, KPI, nhận xét, theo ngày, top, cơ cấu, lịch, top 10. */
const OverviewBody = ({ ov, openCreator }: Props) => {
  const byMonthFile = ov.coverage.source === 'MONTH_FILE';
  const noSeries = byMonthFile ? NO_SERIES_MONTH_FILE : NO_SERIES;
  // File tháng một khoảng trong tháng (vd 01/09–27/09) — không gọi là "cả tháng".
  const monthFilePartial =
    ov.tiktokMonth?.full === false || ov.coverage.daysElapsed < ov.coverage.daysInMonth;
  return (
    <>
      <p className={styles.coverage} aria-live="polite">
        {byMonthFile ? (
          <>
            Theo <strong>file tháng TikTok</strong> (
            {monthFileScope(ov.tiktokMonth?.period, monthFilePartial, ov.coverage.asOfDay)}, không có
            số theo ngày)
          </>
        ) : (
          <>
            Số liệu đến <strong>{fmtDay(ov.coverage.asOfDay)}</strong> · {ov.coverage.daysElapsed}/
            {ov.coverage.daysInMonth} ngày
          </>
        )}{' '}
        · {fmtNum(ov.scope.creators)} Creator
        {ov.scope.creatorKind ? ` ${KIND_LABEL[ov.scope.creatorKind]}` : ''}
        {ov.attention.danger + ov.attention.warn > 0 &&
          ` · ${fmtNum(ov.attention.danger)} cần xử lý, ${fmtNum(ov.attention.warn)} cần để ý`}
      </p>
      {ov.coverage.missingDays.length > 0 && (
        <Alert
          className={styles.section}
          type="warning"
          showIcon
          message={`Thiếu file ngày: ${ov.coverage.missingDays.map(fmtDay).join(', ')}.`}
        />
      )}

      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            label="Kim cương"
            value={fmtKc(ov.kpis.diamonds.value)}
            {...kpiCompareProps(ov.kpis.diamonds, ov)}
            series={ov.kpis.diamonds.series}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            label="Giờ LIVE"
            value={fmtHours(ov.kpis.liveHours.value)}
            {...kpiCompareProps(ov.kpis.liveHours, ov)}
            series={ov.kpis.liveHours.series}
            color="#1F5FA8"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            label="Ngày LIVE hợp lệ"
            value={fmtNum(ov.kpis.liveDays.value)}
            {...kpiCompareProps(ov.kpis.liveDays, ov)}
            series={ov.kpis.liveDays.series}
            color="#0F7A46"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            label="Creator có LIVE"
            value={fmtNum(ov.kpis.activeCreators.value)}
            sub={`trên ${fmtNum(ov.kpis.activeCreators.total)} Creator trong phạm vi`}
            series={ov.kpis.activeCreators.series}
            color="#8E97FD"
          />
        </Col>
      </Row>

      <MoreKpis ov={ov} />

      <div className={styles.section}>
        <InsightsCard insights={ov.insights} max={5} />
      </div>

      <div className={styles.section}>
        {ov.seriesAvailable ? (
          <DailyLine
            days={ov.series.days}
            diamonds={ov.series.diamonds}
            ma7={ov.series.ma7}
            liveHours={ov.series.liveHours}
            activeCreators={ov.series.activeCreators}
            anomalies={ov.series.anomalies}
          />
        ) : (
          <section className={styles.card}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={noSeries} />
          </section>
        )}
      </div>

      {ov.seriesAvailable && (
        <Row gutter={[16, 16]} className={styles.section}>
          <Col xs={24} lg={8}>
            <TopBars
              title="Top tăng (7 ngày)"
              rows={ov.topUp}
              direction="up"
              onSelect={openCreator}
            />
          </Col>
          <Col xs={24} lg={8}>
            <TopBars
              title="Top giảm (7 ngày)"
              rows={ov.topDown}
              direction="down"
              onSelect={openCreator}
            />
          </Col>
          <Col xs={24} lg={8}>
            <AtRiskList rows={ov.atRisk} onSelect={openCreator} />
          </Col>
        </Row>
      )}

      <OverseasSection ov={ov} onSelect={openCreator} />

      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} lg={12}>
          <FansSection ov={ov} />
        </Col>
        <Col xs={24} lg={12}>
          <StatusDonut ov={ov} />
        </Col>
      </Row>

      <EfficiencySection
        efficiency={ov.efficiency}
        top={ov.efficiencyTop}
        onSelect={openCreator}
        scopeCreators={ov.scope.creators}
      />

      <Row gutter={[16, 16]} className={styles.section}>
        {ov.tiktokMonth && (
          <Col xs={24} lg={12}>
            <TiktokMonthPanel ov={ov} />
          </Col>
        )}
        {ov.byAgent && (
          <Col xs={24} lg={12}>
            <AgentTable ov={ov} />
          </Col>
        )}
        <Col xs={24} lg={12}>
          <CohortsCard ov={ov} />
        </Col>
      </Row>

      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} md={12}>
          <MixDonut mix={ov.mix} />
        </Col>
        <Col xs={24} md={12}>
          <section className={styles.card} aria-label="Theo nhóm">
            <h2 className={styles.cardTitle}>
              {ov.byGroup.length ? 'Kim cương theo nhóm' : 'Kim cương theo loại Creator'}
            </h2>
            <Donut
              slices={
                ov.byGroup.length
                  ? ov.byGroup.map((g) => ({ name: g.name, value: g.diamonds }))
                  : ov.byKind.map((k) => ({
                      name: KIND_LABEL[k.kind] || k.kind,
                      value: k.diamonds,
                    }))
              }
            />
          </section>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} lg={12}>
          {ov.seriesAvailable ? (
            <MonthHeatmap
              cells={ov.heatmap}
              asOfDay={ov.coverage.asOfDay}
              showActive
              title="Lịch Kim cương theo ngày"
            />
          ) : (
            <section className={styles.card}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={noSeries} />
            </section>
          )}
        </Col>
        <Col xs={24} lg={12}>
          <RankingList rows={ov.ranking} onSelect={openCreator} />
        </Col>
      </Row>
    </>
  );
};

export default OverviewBody;
